import { budgetReviewStatusEnum } from "../../ilp/list-view/BudgetTaskManagement";
import { isAdvanceDrawWithNoInspection } from "../advanceDrawUtils";

/** Inspection Review statuses that indicate the step is finished. */
export const INSPECTION_REVIEW_COMPLETION_STATUSES = [
  "Approved",
  "Approved Conditionally",
  "Completed"
] as const;

const BUDGET_REVIEW_TASK_NAME = "budgetReview";
const PRIMARY_INSPECTION_TAG_CODES = new Set(["DDDC", "ADRF"]);

export type LoanBudgetReviewTask = {
  taskName?: string;
  taskStatus?: string;
};

export type LoanBudgetReviewTasksPayload = Array<{
  assignedTasks?: LoanBudgetReviewTask[];
}>;

export type BudgetReconcileGateState = {
  budgetReviewStatus: string;
  allDrawDocs: any[];
  budgetReconDisable: boolean;
};

export type BudgetReconcileClickAction =
  | { type: "redirect" }
  | { type: "primaryBudgetNotAvailable" }
  | { type: "budgetReviewConfirmation" }
  | { type: "noPrimaryInspection" };

/** Loan-level budgetReview task status, with draw field as fallback. */
export function resolveLoanBudgetReviewStatus(
  tasks?: LoanBudgetReviewTasksPayload | null,
  drawBudgetReviewStatus?: string | null
): string {
  const fromTask = tasks?.[0]?.assignedTasks?.find(
    (task) => task.taskName === BUDGET_REVIEW_TASK_NAME
  )?.taskStatus;
  return fromTask ?? drawBudgetReviewStatus ?? "";
}

function asDocList(value: unknown): any[] {
  return Array.isArray(value) ? value : [];
}

export function filterPrimaryInspectionDocs(drawDocs: any[] = []): any[] {
  return asDocList(drawDocs).filter(
    (item: any) =>
      item.isPurchasePrimary &&
      Array.isArray(item.tags) &&
      item.tags.some((tag: any) =>
        PRIMARY_INSPECTION_TAG_CODES.has(tag?.tag?.code)
      )
  );
}

/**
 * Resolves Budget Reconciliation button gate state on the draw header.
 * Preserves loan budgetReview status even when the draw has no documents yet,
 * but keeps the button disabled until a primary inspection exists (or the
 * draw is advance/item-wise with no inspection).
 */
export function resolveBudgetReconcileGateState(params: {
  drawType?: string | null;
  drawAmountType?: string | null;
  inspectionReviewStatus?: string | null;
  drawBudgetReviewStatus?: string | null;
  drawDocs?: any[] | null;
  tasks?: LoanBudgetReviewTasksPayload | null;
}): BudgetReconcileGateState {
  const drawDocs = asDocList(params.drawDocs);
  const tasks = asDocList(params.tasks);
  const budgetReviewTaskStatus = resolveLoanBudgetReviewStatus(
    tasks,
    params.drawBudgetReviewStatus
  );
  const isAdvanceNoInspection = isAdvanceDrawWithNoInspection(
    params.drawType ?? undefined,
    drawDocs,
    params.drawAmountType ?? undefined
  );

  if (isAdvanceNoInspection) {
    return {
      budgetReviewStatus: budgetReviewTaskStatus,
      allDrawDocs: [],
      budgetReconDisable: shouldDisableBudgetReconcileForIncompleteInspection(
        params.inspectionReviewStatus
      )
    };
  }

  if (!drawDocs.length) {
    return {
      budgetReviewStatus: budgetReviewTaskStatus,
      allDrawDocs: [],
      budgetReconDisable: true
    };
  }

  if (!tasks.length) {
    return {
      budgetReviewStatus: budgetReviewTaskStatus,
      allDrawDocs: [],
      budgetReconDisable: true
    };
  }

  const filDocsList = filterPrimaryInspectionDocs(drawDocs);
  return {
    budgetReviewStatus: budgetReviewTaskStatus,
    allDrawDocs: filDocsList,
    budgetReconDisable: !filDocsList.length
  };
}

/** Pure click decision for Budget Reconciliation (popups vs redirect). */
export function resolveBudgetReconcileClickAction(params: {
  drawType?: string | null;
  drawAmountType?: string | null;
  drawDocs?: any[] | null;
  budgetReviewStatus: string;
  allDrawDocs?: any[] | null;
}): BudgetReconcileClickAction {
  const drawDocs = asDocList(params.drawDocs);
  const allDrawDocs = asDocList(params.allDrawDocs);
  const advanceDrawWithNoInspection = isAdvanceDrawWithNoInspection(
    params.drawType ?? undefined,
    drawDocs,
    params.drawAmountType ?? undefined
  );
  const status = params.budgetReviewStatus as budgetReviewStatusEnum;

  if (
    advanceDrawWithNoInspection &&
    status === budgetReviewStatusEnum.Approved
  ) {
    return { type: "redirect" };
  }

  const filteredDocsList = filterPrimaryInspectionDocs(allDrawDocs);
  if (
    status === budgetReviewStatusEnum.Approved &&
    filteredDocsList.length
  ) {
    return { type: "redirect" };
  }

  if (
    [
      budgetReviewStatusEnum.LoanSubmitted,
      budgetReviewStatusEnum.BudgetMissing
    ].includes(status)
  ) {
    return { type: "primaryBudgetNotAvailable" };
  }

  if (status !== budgetReviewStatusEnum.Approved) {
    return { type: "budgetReviewConfirmation" };
  }

  return { type: "noPrimaryInspection" };
}

export function isInspectionReviewCompletedStatus(
  status?: string | null
): boolean {
  if (!status) return false;
  return INSPECTION_REVIEW_COMPLETION_STATUSES.includes(
    status.trim() as (typeof INSPECTION_REVIEW_COMPLETION_STATUSES)[number]
  );
}

/** Advance draws with no inspection keep budget reconcile disabled until inspection review completes. */
export function shouldDisableBudgetReconcileForIncompleteInspection(
  inspectionReviewStatus?: string | null
): boolean {
  return !isInspectionReviewCompletedStatus(inspectionReviewStatus);
}

export type LoanDrawReconcileSummary = {
  drawNumber?: string | number;
  inspectionReviewStatus?: string;
};

/** Loan draws keyed by drawId (runtime shape from drawDiscussions store). */
export type DrawDiscussionsByDrawId = Record<
  string,
  { drawDetails?: LoanDrawReconcileSummary }
>;

/** Normalizes drawDiscussions from Redux (typed as array, stored as drawId map). */
export function selectDrawDiscussionsByDrawId(
  drawDiscussions: unknown
): DrawDiscussionsByDrawId {
  if (
    !drawDiscussions ||
    typeof drawDiscussions !== "object" ||
    Array.isArray(drawDiscussions)
  ) {
    return {};
  }
  return drawDiscussions as DrawDiscussionsByDrawId;
}

export function getLoanDrawsFromDiscussions(
  drawDiscussionsHistory?: DrawDiscussionsByDrawId | null
): LoanDrawReconcileSummary[] {
  if (
    !drawDiscussionsHistory ||
    typeof drawDiscussionsHistory !== "object" ||
    Array.isArray(drawDiscussionsHistory)
  ) {
    return [];
  }
  return Object.values(drawDiscussionsHistory)
    .map((entry) => entry?.drawDetails)
    .filter((draw): draw is LoanDrawReconcileSummary => Boolean(draw));
}

/**
 * Lock Budget Reconcile on the current draw when any later draw (higher drawNumber)
 * has Inspection Review in a completion status.
 */
export function shouldLockPastDrawBudgetReconcile(
  currentDrawNumber: string | number | undefined,
  loanDraws: LoanDrawReconcileSummary[]
): boolean {
  const currentNum = Number(currentDrawNumber);
  if (Number.isNaN(currentNum)) return false;

  return loanDraws.some((draw) => {
    const drawNum = Number(draw.drawNumber);
    if (Number.isNaN(drawNum) || drawNum <= currentNum) return false;
    return isInspectionReviewCompletedStatus(draw.inspectionReviewStatus);
  });
}

/**
 * True when the current draw has the highest drawNumber among loan draws.
 * If no other draws are available for comparison, treat the current draw as last.
 */
export function isLastDrawOfLoan(
  currentDrawNumber: string | number | undefined,
  loanDraws: LoanDrawReconcileSummary[]
): boolean {
  const currentNum = Number(currentDrawNumber);
  if (Number.isNaN(currentNum)) return false;

  const drawNumbers = loanDraws
    .map((draw) => Number(draw.drawNumber))
    .filter((drawNum) => !Number.isNaN(drawNum));

  if (!drawNumbers.length) return true;

  return currentNum === Math.max(...drawNumbers);
}

/** Allow unmarking primary inspection on Draw module last draw (with or without BR/IR started). */
export function canToggleOffPrimaryInspection(params: {
  isDrawModule?: boolean;
  isLastDraw?: boolean;
}): boolean {
  return Boolean(params.isDrawModule && params.isLastDraw);
}

/** Allow unmarking primary inspection after BR/IR started — Draw module, last draw only. */
export function canToggleOffPrimaryInspectionAfterReconcile(params: {
  isDrawModule?: boolean;
  isLastDraw?: boolean;
  inspectionReviewStarted?: boolean;
  budgetReconcileApproved?: boolean;
}): boolean {
  return Boolean(
    canToggleOffPrimaryInspection(params) &&
      (params.inspectionReviewStarted || params.budgetReconcileApproved)
  );
}

/** Primary DDDC/ADRF currently driving BR/IR — changes need confirm + reset (SCOW-style). */
export function isInUsePrimaryInspectionDocument(params: {
  tagCode?: string;
  isPurchasePrimary?: boolean;
  isDrawModule?: boolean;
  isLastDraw?: boolean;
  inspectionReviewStarted?: boolean;
  budgetReconcileApproved?: boolean;
}): boolean {
  return Boolean(
    ["DDDC", "ADRF"].includes(params.tagCode || "") &&
      params.isPurchasePrimary &&
      canToggleOffPrimaryInspectionAfterReconcile(params)
  );
}

export const PRIMARY_INSPECTION_RESET_COMMENT =
  "Primary inspection document updated - Budget Reconciliation reset";

export type IrBrTaskHistoryEntry = {
  taskId?: string | number;
  resourceId?: string | number;
  taskStatus?: string;
};

export type IrBrCombinedHistory = {
  /** budgetReconcile tasks (naming inherited from getIRandBRHistory) */
  sortedInspectionHistory?: IrBrTaskHistoryEntry[];
  /** inspectionReview tasks (naming inherited from getIRandBRHistory) */
  sortedBudgetReconcileHistory?: IrBrTaskHistoryEntry[];
};

/**
 * Builds saveReviewAndReconcileStatus payload to reset BR + IR to Pending.
 * Note: getIRandBRHistory swaps list names (budgetReconcile → sortedInspectionHistory).
 */
export function buildPrimaryInspectionStatusResetPayload(
  drawId: string,
  history?: IrBrCombinedHistory | null,
  comment: string = PRIMARY_INSPECTION_RESET_COMMENT
): Array<{
  name: string;
  status: string;
  resourceId: string | number;
  taskId: string | number;
  comment: string;
}> {
  const budgetReconcileTask = history?.sortedInspectionHistory?.[0];
  const inspectionReviewTask = history?.sortedBudgetReconcileHistory?.[0];
  const payload: Array<{
    name: string;
    status: string;
    resourceId: string | number;
    taskId: string | number;
    comment: string;
  }> = [];

  if (budgetReconcileTask?.taskId) {
    payload.push({
      name: "budgetReconcile",
      status: "Pending",
      resourceId: budgetReconcileTask.resourceId || drawId,
      taskId: budgetReconcileTask.taskId,
      comment
    });
  }
  if (inspectionReviewTask?.taskId) {
    payload.push({
      name: "inspectionReview",
      status: "Pending",
      resourceId: inspectionReviewTask.resourceId || drawId,
      taskId: inspectionReviewTask.taskId,
      comment
    });
  }
  return payload;
}
